import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, ArrowUpDown, Search } from "lucide-react";
import { ROLE_LABEL, WEEKDAYS } from "@/utils/status";
import { useAuth } from "@/context/AuthContext";

const COLORS = [
  // Blu/viola
  "#2563EB", "#4F46E5", "#7C3AED", "#9333EA", "#0284C7", "#0891B2",
  // Verdi/teal
  "#059669", "#0D9488", "#14B8A6", "#65A30D", "#84CC16",
  // Giallo/arancio
  "#EAB308", "#F59E0B", "#D97706", "#EA580C", "#F97316",
  // Rossi/magenta
  "#E11D48", "#DC2626", "#DB2777", "#EC4899", "#F43F5E",
  // Marroni/neutri (per differenziare ulteriormente)
  "#B45309", "#78350F", "#475569",
];

const ROLE_ORDER = { admin: 0, manager: 1, receptionist: 2, collaborator: 3, trainer: 4 };

const calendarsOf = (u) => {
  if (u.role === "collaborator") return "Consulenti";
  if (u.role === "trainer") return "Trainers";
  if (u.role === "manager") {
    const c = [u.show_in_consulenti !== false && "Consulenti", u.show_in_trainers !== false && "Trainers"].filter(Boolean);
    return c.length ? c.join(", ") : "—";
  }
  return "—";
};

const SORTERS = {
  last_name: (u) => (u.last_name ? `${u.last_name} ${u.first_name || ""}` : `\uFFFF${u.first_name || ""}`).toLowerCase(),
  first_name: (u) => `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase(),
  email: (u) => (u.email || "").toLowerCase(),
  role: (u) => `${ROLE_ORDER[u.role] ?? 9}|${(u.last_name || "").toLowerCase()}`,
  calendars: (u) => calendarsOf(u),
  active: (u) => `${u.active ? 0 : 1}|${(u.last_name || "").toLowerCase()}`,
};

function SortHead({ k, label, sort, setSort }) {
  const active = sort.key === k;
  const Icon = !active ? ArrowUpDown : sort.dir === 1 ? ArrowUp : ArrowDown;
  return (
    <TableHead>
      <button
        onClick={() => setSort(active ? { key: k, dir: -sort.dir } : { key: k, dir: 1 })}
        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${active ? "text-foreground font-semibold" : ""}`}
        data-testid={`team-sort-${k}`}
      >
        {label} <Icon className="h-3.5 w-3.5" />
      </button>
    </TableHead>
  );
}

export default function CollaboratorsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sort, setSort] = useState({ key: "last_name", dir: 1 });
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const fn = SORTERS[sort.key];
    return users
      .filter((u) => roleFilter === "all" || u.role === roleFilter)
      .filter((u) => !s || `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(s))
      .sort((a, b) => fn(a).localeCompare(fn(b), "it") * sort.dir);
  }, [users, sort, q, roleFilter]);

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ email: "", first_name: "", last_name: "", role: "collaborator", color: COLORS[0], password: "", active: true, working_hours: null }); setOpen(true); };
  const openEdit = (u) => { setEditing({ ...u, password: "" }); setOpen(true); };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Collaboratore eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8 h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Collaboratori</h1>
          <p className="text-muted-foreground text-sm mt-1">Gestisci account, ruoli e fasce orarie lavorative.</p>
        </div>
        {isAdmin && (
          <Button onClick={openNew} data-testid="add-collaborator-button">
            <Plus className="h-4 w-4 mr-2" /> Nuovo Collaboratore
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, cognome o email" className="pl-9 w-72" data-testid="team-search-input" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-48" data-testid="team-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i ruoli</SelectItem>
            {Object.keys(ROLE_ORDER).map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground" data-testid="team-count">{rows.length} membri</span>
      </div>

      <div className="bg-card border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <SortHead k="last_name" label="Cognome" sort={sort} setSort={setSort} />
              <SortHead k="first_name" label="Nome" sort={sort} setSort={setSort} />
              <SortHead k="email" label="Email" sort={sort} setSort={setSort} />
              <SortHead k="role" label="Ruolo" sort={sort} setSort={setSort} />
              <SortHead k="calendars" label="Calendari" sort={sort} setSort={setSort} />
              <TableHead>Colore</TableHead>
              <SortHead k="active" label="Stato" sort={sort} setSort={setSort} />
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id} data-testid={`collaborator-row-${u.id}`}>
                <TableCell className="font-semibold">{u.last_name || "—"}</TableCell>
                <TableCell className="font-medium">{u.first_name}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell><Badge variant="secondary">{ROLE_LABEL[u.role]}</Badge></TableCell>
                <TableCell className="text-sm text-muted-foreground">{calendarsOf(u)}</TableCell>
                <TableCell><span className="inline-block h-4 w-4 rounded-full" style={{ backgroundColor: u.color }} /></TableCell>
                <TableCell>{u.active ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Attivo</Badge> : <Badge variant="outline">Disattivo</Badge>}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-collaborator-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                  {isAdmin && u.id !== user?.id && (
                    <Button variant="ghost" size="icon" onClick={() => remove(u)} className="text-rose-600" data-testid={`delete-collaborator-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <CollaboratorDialog
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        setEditing={setEditing}
        onSaved={load}
        isAdmin={isAdmin}
        isManager={user?.role === "manager"}
        currentUserId={user?.id}
      />
    </div>
  );
}

function CollaboratorDialog({ open, onOpenChange, editing, setEditing, onSaved, isAdmin, isManager, currentUserId }) {
  const [saving, setSaving] = useState(false);
  if (!editing) return null;
  const isNew = !editing.id;

  const wh = editing.working_hours || {
    monday: [{start:"09:00",end:"13:00"},{start:"14:00",end:"18:00"}],
    tuesday: [{start:"09:00",end:"13:00"},{start:"14:00",end:"18:00"}],
    wednesday: [{start:"09:00",end:"13:00"},{start:"14:00",end:"18:00"}],
    thursday: [{start:"09:00",end:"13:00"},{start:"14:00",end:"18:00"}],
    friday: [{start:"09:00",end:"13:00"},{start:"14:00",end:"18:00"}],
    saturday: [], sunday: [],
  };

  const setSlot = (day, idx, field, val) => {
    const next = { ...wh, [day]: [...(wh[day]||[])] };
    next[day][idx] = { ...next[day][idx], [field]: val };
    setEditing({ ...editing, working_hours: next });
  };
  const addSlot = (day) => {
    const next = { ...wh, [day]: [...(wh[day]||[]), { start: "09:00", end: "13:00" }] };
    setEditing({ ...editing, working_hours: next });
  };
  const removeSlot = (day, idx) => {
    const next = { ...wh, [day]: (wh[day]||[]).filter((_, i) => i !== idx) };
    setEditing({ ...editing, working_hours: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        if (!editing.email || !editing.password || !editing.first_name?.trim()) {
          toast.error("Compila nome, email e password."); setSaving(false); return;
        }
        await api.post("/users", {
          email: editing.email, password: editing.password,
          first_name: editing.first_name, last_name: editing.last_name || "",
          role: editing.role, color: editing.color,
        });
        toast.success("Collaboratore creato");
      } else if (!isAdmin && editing.id !== currentUserId) {
        await api.patch(`/users/${editing.id}`, { working_hours: editing.working_hours || wh });
        toast.success("Fasce orarie aggiornate");
      } else {
        const payload = {
          first_name: editing.first_name, last_name: editing.last_name || "", color: editing.color,
        };
        if (isAdmin || isManager) payload.working_hours = editing.working_hours || wh;
        if (isAdmin) {
          payload.role = editing.role;
          payload.active = editing.active;
          // Persist manager calendar visibility toggles.
          // Default to true if the field was never touched on this record.
          payload.show_in_consulenti = editing.show_in_consulenti !== false;
          payload.show_in_trainers = editing.show_in_trainers !== false;
        }
        if (editing.password) payload.password = editing.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Collaboratore aggiornato");
      }
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isNew ? "Nuovo Collaboratore" : "Modifica Collaboratore"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Cognome</Label>
            <Input value={editing.last_name || ""} onChange={(e) => setEditing({ ...editing, last_name: e.target.value })} data-testid="collab-last-name-input" />
          </div>
          <div>
            <Label>Nome</Label>
            <Input value={editing.first_name || ""} onChange={(e) => setEditing({ ...editing, first_name: e.target.value })} data-testid="collab-name-input" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} disabled={!isNew} data-testid="collab-email-input" />
          </div>
          <div>
            <Label>{isNew ? "Password" : "Nuova password (opzionale)"}</Label>
            <Input type="password" value={editing.password || ""} onChange={(e) => setEditing({ ...editing, password: e.target.value })} data-testid="collab-password-input" />
          </div>
          <div>
            <Label>Ruolo</Label>
            <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })} disabled={!isAdmin}>
              <SelectTrigger data-testid="collab-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="collaborator">Consulente</SelectItem>
                <SelectItem value="trainer">Trainer</SelectItem>
                <SelectItem value="receptionist">Receptionist</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            {editing.role === "admin" ? (
              <div className="text-xs text-muted-foreground italic bg-slate-50 border border-dashed rounded-md px-3 py-2">
                Gli account Admin non appaiono nei calendari: colore identificativo non necessario.
              </div>
            ) : (
              <>
                <Label>Colore identificativo</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {COLORS.map((c) => (
                    <button key={c}
                      onClick={() => setEditing({ ...editing, color: c })}
                      className={`h-8 w-8 rounded-full ring-offset-2 ${editing.color === c ? "ring-2 ring-slate-900" : ""}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </>
            )}
          </div>
          {isAdmin && !isNew && (
            <div className="col-span-2 flex items-center gap-2">
              <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
              <span className="text-sm">Account attivo</span>
            </div>
          )}
          {isAdmin && editing.role === "manager" && (
            <div className="col-span-2 border-t pt-3 mt-1">
              <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground mb-2">Visibilità Manager nei Calendari</div>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="manager-show-consulenti-toggle">
                  <Switch
                    checked={editing.show_in_consulenti !== false}
                    onCheckedChange={(v) => setEditing({ ...editing, show_in_consulenti: v })}
                  />
                  <span className="text-sm">Mostra nel Calendario Consulenti</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="manager-show-trainers-toggle">
                  <Switch
                    checked={editing.show_in_trainers !== false}
                    onCheckedChange={(v) => setEditing({ ...editing, show_in_trainers: v })}
                  />
                  <span className="text-sm">Mostra nel Calendario Trainers</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {editing.role !== "admin" && (
          <div className="mt-4">
            <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground mb-2">Fasce Orarie Lavorative</div>
            <div className="space-y-2">
              {WEEKDAYS.map(({ key, label }) => (
                <div key={key} className="flex items-start gap-3 border rounded-md p-2" data-testid={`working-hours-${key}`}>
                  <div className="w-12 font-semibold text-sm pt-1.5">{label}</div>
                  <div className="flex-1 space-y-1">
                    {(wh[key] || []).map((slot, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Input type="time" value={slot.start} className="w-28" onChange={(e) => setSlot(key, idx, "start", e.target.value)} />
                        <span className="text-muted-foreground">–</span>
                        <Input type="time" value={slot.end} className="w-28" onChange={(e) => setSlot(key, idx, "end", e.target.value)} />
                        <Button variant="ghost" size="icon" onClick={() => removeSlot(key, idx)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => addSlot(key)}>+ fascia</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="save-collaborator-button">{saving ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
